import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError, errorText, type DisplaySnapshotDto } from "../api/client";
import { PersonWeekGrid } from "../components/PersonWeekGrid";
import { formatLongDate, relativeTime } from "../format";
import { noteRevision, startRealtime, useConnectionStatus } from "../realtime/realtime";

/**
 * Wanddisplay: gepaartes Gerät mit eingeschränkter Berechtigung. Zeigt Woche, nächste Termine und kompakte
 * Einkaufsliste, Nachtmodus, Auto-Reconnect und gut sichtbaren Offline-/Aktualisierungsstatus.
 */
export function DisplayPage() {
  const qc = useQueryClient();
  const snapshot = useQuery({
    queryKey: ["display"],
    queryFn: async () => {
      const s = await api<DisplaySnapshotDto>("/api/v1/display/snapshot");
      noteRevision(s.revision);
      return s;
    },
    retry: (n, e) => !(e instanceof ApiError && e.status === 401) && n < 3,
    refetchInterval: 5 * 60_000,
  });
  const unpaired = snapshot.error instanceof ApiError && snapshot.error.status === 401;

  useEffect(() => {
    if (!snapshot.data) return;
    return startRealtime(qc, { display: true });
  }, [Boolean(snapshot.data), qc]); // eslint-disable-line react-hooks/exhaustive-deps

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const night = now.getHours() >= 21 || now.getHours() < 6;

  if (unpaired) return <PairForm onPaired={() => void snapshot.refetch()} />;
  if (!snapshot.data) return <div className="display center">{snapshot.isError ? errorText(snapshot.error) : "Lade …"}</div>;

  const s = snapshot.data;

  return (
    <div className={`display${night ? " night" : ""}`}>
      <header className="display-head">
        <div>
          <h1>{s.familyName}</h1>
          <p>{formatLongDate(s.today)} · {now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</p>
        </div>
        <DisplayStatus generatedAt={s.generatedAt} stale={snapshot.isError} />
      </header>
      <div className="display-grid">
        <section className="week-persons" aria-label="Woche nach Personen">
          <PersonWeekGrid
            variant="display"
            persons={s.persons.map((p) => ({ id: p.id, name: p.name, color: p.color }))}
            from={s.today}
            today={s.today}
            events={s.events.map((e) => ({ key: e.occurrenceId, title: e.title, start: e.start, end: e.end, allDay: e.allDay, personIds: e.personIds, createdByPersonId: e.createdByPersonId, source: e.source }))}
          />
        </section>
        <aside className="shopping-mini">
          <h2>Einkauf ({s.shoppingTotal})</h2>
          <ul>
            {s.shopping.map((i, n) => <li key={n}>{i.name}{i.quantity ? <span className="muted"> · {i.quantity}</span> : null}</li>)}
          </ul>
          {s.shoppingTotal > s.shopping.length && <p className="muted">… und {s.shoppingTotal - s.shopping.length} weitere</p>}
        </aside>
      </div>
    </div>
  );
}

function DisplayStatus({ generatedAt, stale }: { generatedAt: string; stale: boolean }) {
  const status = useConnectionStatus();
  const offline = stale || status.state === "offline" || !navigator.onLine;
  return (
    <div className={`display-status${offline ? " offline" : ""}`} role="status">
      {offline ? "⚠ Offline – " : status.state === "online" ? "● Live – " : "○ Verbinde – "}
      Stand {relativeTime(generatedAt)}
    </div>
  );
}

function PairForm({ onPaired }: { onPaired: () => void }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api("/api/v1/devices/pair", { body: { code } });
      onPaired();
    } catch (err) {
      setError(errorText(err));
    }
  };
  return (
    <main className="display center">
      <form className="card pair" onSubmit={submit}>
        <h1>Display koppeln</h1>
        <p>Kopplungscode aus „Einstellungen › Anzeigegeräte“ eingeben.</p>
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ABCD-EFGH" autoFocus maxLength={12} aria-label="Kopplungscode" />
        {error && <p className="error">{error}</p>}
        <button className="btn primary">Koppeln</button>
      </form>
    </main>
  );
}
